import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarCheck,
  Calendar,
  UserCircle,
  LogOut,
  Smartphone,
} from 'lucide-react';
import { UserDetails, UserHolidays, UserLogout } from '../../../services/oprations/User_Bussiness_Logic';
import UserProfile from '../../../components/employee/Dashboard/Profile/UserProfile';
import LeaveManagement from '../../../components/employee/Dashboard/LeaveManagement/Leave';
import Attendance from '../../../components/employee/Dashboard/Attendance/Attendance';
import Analytics from '../../../components/employee/Dashboard/Analytics/Analytics';
import Devices from '../../../components/employee/Dashboard/Devices/Devices';
import { useTheme } from '../../../Context/themeContext';

// ── nav items ─────────────────────────────────────────────────────────────────

const navItems = [
  { id: 'analytics',      label: 'Analytics',     icon: BarChart3 },
  { id: 'attendance',     label: 'Attendance',    icon: CalendarCheck },
  { id: 'leavemanagement',label: 'Leave',         icon: Calendar },
  { id: 'Devices',        label: 'Devices',       icon: Smartphone },
  { id: 'profile',        label: 'Profile',       icon: UserCircle },
];

// ── component ─────────────────────────────────────────────────────────────────

const UserDashboard = () => {
  const { theme } = useTheme();
  const token     = localStorage.getItem('token');
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const [activeTab, setActiveTab] = useState('analytics');

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-black' : 'bg-gray-50';
  const bgSidebar = isDark ? 'bg-gray-900' : 'bg-white';
  const bgMobileNav = isDark ? 'bg-gray-900' : 'bg-white';
  //const bgTopBar = isDark ? 'bg-gray-900' : 'bg-white';
  
  const bgHover = isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50';
  const bgActive = isDark ? 'bg-blue-500/10' : 'bg-blue-100/20';
  const bgIconActive = isDark ? 'bg-blue-500/20' : 'bg-blue-100/30';
  
  const borderColor = isDark ? 'border-gray-800' : 'border-gray-200';
  const borderTop = isDark ? 'border-gray-800' : 'border-gray-200';
  //const borderTab = isDark ? 'border-gray-800' : 'border-gray-200';
  
  //const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';
  //const textMuted = isDark ? 'text-gray-500' : 'text-gray-400';
  const textActive = isDark ? 'text-blue-400' : 'text-blue-600';
  const textInactive = isDark ? 'text-gray-400 dark:text-gray-500' : 'text-gray-400';
  
  //const textGradient = isDark ? 'from-blue-400 to-gray-900': 'from-blue-500 to-gray-800';
  
  const iconActive = isDark ? 'text-blue-400' : 'text-blue-500';
  const iconInactive = isDark ? 'text-gray-500 group-hover:text-blue-400' : 'text-gray-500 group-hover:text-blue-500';
  
  const shadowColor = isDark ? 'shadow-[0_-2px_12px_rgba(0,0,0,0.3)]' : 'shadow-[0_-2px_12px_rgba(0,0,0,0.06)]';
  
  const bgIndicator = isDark ? 'bg-blue-400' : 'bg-blue-500';
  const bgLogoutHover = isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50';

  // auth guard
  useEffect(() => {
    if (!token) navigate('/', { replace: true });
  }, [token, navigate]);

  // initial data fetch
  useEffect(() => {
    if (token) {
      dispatch(UserDetails(token));
      dispatch(UserHolidays(token));
    }
  }, [token, dispatch]);

  const handleLogout = () => {
    dispatch(UserLogout(navigate));
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'analytics':      return <Analytics />;
      case 'attendance':     return <Attendance />;
      case 'leavemanagement':return <LeaveManagement />;
      case 'Devices':        return <Devices />;
      case 'profile':        return <UserProfile />;
      default:               return <Analytics />;
    }
  };

  return (
    <div className={`min-h-screen pt-20 transition-colors duration-300 ${bgPrimary}`}>

      {/* ════════════════════════════════════════════════════
          LAYOUT: sidebar (md+) + main content
      ════════════════════════════════════════════════════ */}
      <div className="flex">

        {/* ── Sidebar — always visible on md+ ── */}
        <aside className={`sticky top-20 z-40 hidden w-64 h-[calc(100vh-5rem)] ${bgSidebar} border-r ${borderColor} shadow-sm md:flex md:flex-col shrink-0 transition-colors duration-300`}>
          {/* Nav links */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                  activeTab === id
                    ? `${bgActive} ${textActive}`
                    : `${textSecondary} ${bgHover}`
                }`}
              >
                <Icon className={`w-5 h-5 transition-colors ${
                  activeTab === id
                    ? iconActive
                    : `${iconInactive}`
                }`} />
                <span className="font-medium">{label}</span>
                {activeTab === id && (
                  <div className={`w-1 h-6 ml-auto ${bgIndicator} rounded-full`} />
                )}
              </button>
            ))}
          </nav>

          {/* Logout */}
          <div className={`p-4 border-t ${borderColor}`}>
            <button
              onClick={handleLogout}
              className={`flex items-center justify-center w-full gap-3 px-4 py-2 ${textSecondary} transition-all duration-200 rounded-lg ${bgLogoutHover} group`}
            >
              <span className="font-medium">Logout</span>
              <LogOut className={`w-5 h-5 transition-colors group-hover:text-blue-500 ${isDark ? 'dark:group-hover:text-blue-400' : ''}`} />
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0 min-h-screen">
          {/* Page content */}
          <div className="p-4 pb-24 md:p-6 lg:p-8 md:pb-8">
            <div className="mx-auto max-w-7xl">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>

      {/* ════════════════════════════════════════════════════
          BOTTOM NAVIGATION — mobile only (hidden on md+)
      ════════════════════════════════════════════════════ */}
      <nav className={`
        fixed bottom-0 left-0 right-0
        z-50
        flex md:hidden
        ${bgMobileNav}
        border-t ${borderTop}
        ${shadowColor}
        transition-colors duration-300
      `}>
        {navItems.map(({ id, label, icon: Icon }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors duration-200 ${
                isActive
                  ? textActive
                  : textInactive
              }`}
            >
              <div className={`p-1 rounded-lg transition-all duration-200 ${
                isActive ? bgIconActive : ''
              }`}>
                <Icon size={20} />
              </div>
              <span className={`text-[10px] font-medium leading-tight transition-all duration-200 ${
                isActive ? textActive : textSecondary
              }`}>
                {label}
              </span>
            </button>
          );
        })}

        {/* Logout in bottom nav on mobile */}
        <button
          onClick={handleLogout}
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 ${textSecondary} transition-colors`}
        >
          <div className="p-1 rounded-lg">
            <LogOut size={20} />
          </div>
          <span className="text-[10px] font-medium leading-tight">Logout</span>
        </button>
      </nav>

    </div>
  );
};

export default UserDashboard;
